import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, Zap, Lock, CreditCard, ChevronRight } from 'lucide-react';
const plans = [{
  id: 'esencial',
  icon: '📋',
  name: 'PLAN ESENCIAL',
  description: '3 Tomos + 3 Bonos',
  originalPrice: '$59.990',
  price: '$19.990',
  highlighted: false,
  badges: [],
  checkoutUrl: 'https://checkout.empezahoy.shop/cart/48190648123624:1'
}, {
  id: 'pro',
  icon: '🏆',
  name: 'PLAN PRO',
  description: '6 Tomos + 6 Bonos completos + Sistema de Diagnóstico Rápido DSM-5',
  originalPrice: '$79.990',
  price: '$27.990',
  highlighted: true,
  badges: ['⭐ MÁS ELEGIDO', 'AHORRÁS $55.000'],
  checkoutUrl: 'https://checkout.empezahoy.shop/cart/48190664048872:1'
}, {
  id: 'elite',
  icon: '📚',
  name: 'PLAN ELITE 360°',
  description: 'Plan PRO + Business Pro Psiquiátrico',
  originalPrice: '$99.990',
  price: '$35.990',
  highlighted: false,
  badges: [],
  checkoutUrl: 'https://checkout.empezahoy.shop/cart/48190668570856:1'
}];
const planDetails: Record<string, {
  title: string;
  intro?: {
    icon: string;
    text: string;
  };
  items: {
    icon: string;
    text: string;
    highlight?: boolean;
  }[];
}> = {
  esencial: {
    title: '¿Qué incluye el Plan Esencial?',
    items: [{
      icon: '📘',
      text: 'Tomo I – Historia Clínica Psiquiátrica Profesional'
    }, {
      icon: '📗',
      text: 'Tomo II – Protocolo de Entrevista Inicial Estructurada'
    }, {
      icon: '📙',
      text: 'Tomo III – Escudo Legal del Psiquiatra'
    }, {
      icon: '🎁',
      text: 'Bono 1 – Guía de Psicoeducación para Familias'
    }, {
      icon: '🎁',
      text: 'Bono 2 – Checklist de Consulta Diaria'
    }, {
      icon: '🎁',
      text: 'Bono 3 – Modelo de Consentimiento Informado'
    }, {
      icon: '🔄',
      text: 'Acceso de por vida + actualizaciones gratuitas'
    }]
  },
  pro: {
    title: '¿Qué incluye el Plan PRO?',
    intro: {
      icon: '✨',
      text: 'Todo lo del Plan Esencial +'
    },
    items: [{
      icon: '📔',
      text: 'Tomo IV – Planificador de Farmacoterapia Avanzada'
    }, {
      icon: '📓',
      text: 'Tomo V – Guía de Urgencias y Crisis en Consultorio'
    }, {
      icon: '📕',
      text: 'Tomo VI – Portafolio de Informes Forenses y Legales'
    }, {
      icon: '🎁',
      text: 'Bono 4 – Protocolo Anti-Burnout para Psiquiatras'
    }, {
      icon: '🎁',
      text: 'Bono 5 – Script de Cobro y Gestión de Pacientes Morosos'
    }, {
      icon: '🎁',
      text: 'Bono 6 – Pack de 20 Plantillas de Instagram para Psiquiatras'
    }, {
      icon: '⚡',
      text: 'Sistema de Diagnóstico Rápido DSM-5 Ready (UPSELL exclusivo)',
      highlight: true
    }, {
      icon: '🔄',
      text: 'Acceso de por vida + actualizaciones gratuitas'
    }]
  },
  elite: {
    title: '¿Qué incluye el Plan Elite 360°?',
    intro: {
      icon: '✨',
      text: 'Todo lo del Plan PRO +'
    },
    items: [{
      icon: '💼',
      text: 'Business Pro Psiquiátrico – Sistema completo de gestión de consultorio'
    }, {
      icon: '📊',
      text: 'Plantillas de presupuesto y facturación profesional'
    }, {
      icon: '📈',
      text: 'Estrategia de captación y fidelización de pacientes'
    }, {
      icon: '🎯',
      text: 'Plan de marketing digital para psiquiatras'
    }, {
      icon: '🔄',
      text: 'Acceso de por vida + actualizaciones gratuitas'
    }]
  }
};
export function PricingSection() {
  const [selectedPlan, setSelectedPlan] = useState('pro');
  const currentPlan = plans.find(p => p.id === selectedPlan) || plans[1];
  const currentDetails = planDetails[selectedPlan] || planDetails.pro;
  return <section id="pricing" className="py-12 md:py-24 bg-navy relative scroll-mt-10" data-id="element-92">
      {/* Urgency bar — full width, embedded */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} className="relative overflow-hidden py-4 md:py-5 mb-8 md:mb-12" data-id="element-93">
        {/* Fire background effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-red-900 via-red-700 to-orange-600" data-id="element-94"></div>
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-20 mix-blend-overlay" data-id="element-95"></div>
        <div className="absolute -top-16 -left-16 w-32 h-32 bg-orange-500 rounded-full blur-[60px] animate-pulse" data-id="element-96"></div>
        <div className="absolute -bottom-16 -right-16 w-40 h-40 bg-yellow-500 rounded-full blur-[70px] animate-pulse" style={{
        animationDelay: '1s'
      }} data-id="element-97"></div>

        <div className="relative z-10 text-center max-w-2xl mx-auto px-4 sm:px-6" data-id="element-98">
          <div className="inline-flex items-center justify-center gap-1.5 bg-black/30 px-3 py-1 rounded-full mb-2.5 border border-red-400/30" data-id="element-99">
            <span className="animate-pulse text-sm" data-id="element-100">🔥</span>
            <p className="text-white font-bold uppercase tracking-wider text-[10px] md:text-xs" data-id="element-101">
              Promoción por tiempo limitado
            </p>
            <span className="animate-pulse text-sm" data-id="element-102">🔥</span>
          </div>

          <div className="flex items-center justify-center gap-2 text-xs md:text-sm text-red-100 mb-2 font-medium" data-id="element-103">
            <span data-id="element-104">Cupos disponibles agotándose</span>
            <span className="font-bold text-white bg-red-950/50 px-1.5 py-0.5 rounded text-xs" data-id="element-105">
              80% tomados
            </span>
          </div>

          <div className="max-w-sm mx-auto bg-black/40 rounded-full h-2 mb-2 border border-red-900/50 overflow-hidden" data-id="element-106">
            <motion.div initial={{
            width: 0
          }} whileInView={{
            width: '80%'
          }} transition={{
            duration: 1.5,
            ease: 'easeOut'
          }} className="bg-gradient-to-r from-orange-500 to-yellow-400 h-full rounded-full relative" data-id="element-107">
              <div className="absolute inset-0 bg-white/20 animate-[shimmer_2s_infinite]" data-id="element-108"></div>
            </motion.div>
          </div>

          <p className="text-[10px] md:text-xs text-red-100/70 max-w-md mx-auto" data-id="element-109">
            Últimos cupos a precio promocional — una vez agotados, vuelve al
            precio original.
          </p>
        </div>
      </motion.div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6" data-id="element-110">
        {/* Title */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-8 md:mb-10" data-id="element-111">
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-white mb-3" data-id="element-112">
            Elegí tu plan
          </h2>
          <p className="text-base md:text-xl text-gray-300" data-id="element-113">
            Acceso inmediato y de por vida. Pago único, sin suscripciones.
          </p>
        </motion.div>

        {/* Plan Cards — Stacked */}
        <div className="flex flex-col gap-3 md:gap-4" data-id="element-114">
          {plans.map((plan, index) => {
          const isSelected = selectedPlan === plan.id;
          return <motion.div key={plan.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} onClick={() => setSelectedPlan(plan.id)} className={`relative rounded-xl p-4 md:p-5 cursor-pointer transition-all duration-300 ${plan.highlighted ? 'border-2 border-gold bg-navy-light shadow-[0_0_20px_rgba(201,168,76,0.15)]' : isSelected ? 'border-2 border-gold/50 bg-navy-light/60' : 'border border-navy-lighter bg-navy-light/40 hover:border-gray-600'}`} data-id="element-115">
                <div className="flex items-center gap-3 md:gap-4" data-id="element-116">
                  {/* Radio button */}
                  <div className={`w-5 h-5 md:w-6 md:h-6 rounded-full border-2 shrink-0 flex items-center justify-center transition-colors ${isSelected ? 'border-gold' : 'border-gray-600'}`} data-id="element-117">
                    {isSelected && <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-gold" data-id="element-118"></div>}
                  </div>

                  {/* Plan info */}
                  <div className="flex-grow min-w-0" data-id="element-119">
                    <div className="flex items-center gap-2 flex-wrap" data-id="element-120">
                      <span className="text-lg md:text-xl" data-id="element-121">{plan.icon}</span>
                      <h3 className="text-sm md:text-lg font-bold text-white tracking-wide" data-id="element-122">
                        {plan.name}
                      </h3>
                    </div>

                    {plan.badges.length > 0 && <div className="flex items-center gap-2 mt-1.5 flex-wrap" data-id="element-123">
                        {plan.badges.map((badge, i) => <span key={i} className={`text-[10px] md:text-xs font-bold px-2 py-0.5 rounded-full ${i === 0 ? 'bg-gold text-navy' : 'bg-green-900/60 text-green-400 border border-green-500/30'}`} data-id="element-124">
                            {badge}
                          </span>)}
                      </div>}

                    <p className="text-xs md:text-sm text-gray-400 mt-1" data-id="element-125">
                      {plan.description}
                    </p>
                  </div>

                  {/* Price */}
                  <div className="text-right shrink-0" data-id="element-126">
                    <span className="block text-xs md:text-sm text-gray-500 line-through" data-id="element-127">
                      {plan.originalPrice}
                    </span>
                    <span className={`text-2xl md:text-3xl font-bold ${plan.highlighted ? 'text-gold' : 'text-white'}`} data-id="element-128">
                      {plan.price}
                    </span>
                  </div>
                </div>
              </motion.div>;
        })}
        </div>

        {/* CTA Button */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-8" data-id="element-129">
          <a href={currentPlan.checkoutUrl} target="_blank" rel="noopener noreferrer" className="group relative flex items-center justify-center w-full py-4 md:py-5 px-6 text-base md:text-xl font-bold text-navy bg-gold rounded-xl overflow-hidden transition-all hover:scale-[1.02] hover:shadow-[0_0_30px_rgba(201,168,76,0.4)] active:scale-[0.98]" data-id="element-130">
            <span className="relative z-10 flex items-center" data-id="element-131">
              SÍ, QUIERO {currentPlan.name} →
            </span>
            <div className="absolute inset-0 h-full w-full bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" data-id="element-132"></div>
          </a>

          <p className="text-center text-xs md:text-sm text-gray-400 italic mt-3" data-id="element-133">
            La opción más elegida por psiquiatras que buscan documentación
            completa y respaldo legal total
          </p>
        </motion.div>

        {/* Trust Badges */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-6 border border-navy-lighter rounded-xl py-3 px-4" data-id="element-134">
          <div className="flex flex-wrap justify-center gap-3 md:gap-6 text-xs md:text-sm text-gray-400" data-id="element-135">
            <div className="flex items-center gap-1.5" data-id="element-136">
              <Lock className="w-3.5 h-3.5 text-gold" data-id="element-137" /> Pago seguro
            </div>
            <div className="flex items-center gap-1.5" data-id="element-138">
              <Zap className="w-3.5 h-3.5 text-gold" data-id="element-139" /> Acceso en 5 min
            </div>
            <div className="flex items-center gap-1.5" data-id="element-140">
              <Shield className="w-3.5 h-3.5 text-gold" data-id="element-141" /> Garantía 7 días
            </div>
            <div className="flex items-center gap-1.5" data-id="element-142">
              <CreditCard className="w-3.5 h-3.5 text-gold" data-id="element-143" /> Mercado Pago
            </div>
          </div>
        </motion.div>

        {/* Plan Details — Dynamic */}
        <motion.div key={selectedPlan} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="mt-16 md:mt-20 bg-navy-light/50 border border-gold/20 rounded-2xl p-6 md:p-10" data-id="element-144">
          <h3 className="text-2xl md:text-3xl font-serif font-bold text-center text-white mb-6 md:mb-8" data-id="element-145">
            {currentDetails.title}
          </h3>

          <div className="space-y-3 md:space-y-4" data-id="element-146">
            {currentDetails.intro && <div className="flex items-start gap-3 pb-3 border-b border-white/5" data-id="element-147">
                <span className="text-lg md:text-xl" data-id="element-148">
                  {currentDetails.intro.icon}
                </span>
                <span className="text-base md:text-lg font-bold text-white" data-id="element-149">
                  {currentDetails.intro.text}
                </span>
              </div>}
            {currentDetails.items.map((item, i) => <div key={i} className={`flex items-start gap-3 ${i < currentDetails.items.length - 1 ? 'pb-3 border-b border-white/5' : 'pt-1'}`} data-id="element-150">
                <span className="text-lg md:text-xl" data-id="element-151">{item.icon}</span>
                <span className={`text-sm md:text-base ${item.highlight ? 'text-gold font-medium' : 'text-gray-200'} ${i === currentDetails.items.length - 1 ? 'font-medium' : ''}`} data-id="element-152">
                  {item.text}
                </span>
              </div>)}
          </div>

          <div className="mt-8 md:mt-10 text-center" data-id="element-153">
            <p className="text-gold font-medium text-sm md:text-base mb-5" data-id="element-154">
              La opción más elegida por psiquiatras que ejercen en consultorio
              privado e institucional
            </p>
            <a href={currentPlan.checkoutUrl} target="_blank" rel="noopener noreferrer" className="group relative inline-flex items-center justify-center w-full sm:w-auto px-6 py-3 md:px-8 md:py-4 text-base md:text-lg font-bold text-navy bg-gold rounded-lg overflow-hidden transition-all hover:scale-105" data-id="element-155">
              <span className="relative z-10 flex items-center" data-id="element-156">
                Acceder al {currentPlan.name}
                <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-157" />
              </span>
              <div className="absolute inset-0 h-full w-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" data-id="element-158"></div>
            </a>
            <div className="flex flex-wrap justify-center gap-3 mt-4 text-xs text-gray-400" data-id="element-159">
              <span className="flex items-center gap-1" data-id="element-160">
                <Lock className="w-3 h-3" data-id="element-161" /> Pago seguro
              </span>
              <span className="flex items-center gap-1" data-id="element-162">
                <Zap className="w-3 h-3" data-id="element-163" /> Acceso inmediato
              </span>
              <span className="flex items-center gap-1" data-id="element-164">
                <Shield className="w-3 h-3" data-id="element-165" /> Garantía 7 días
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}